import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "../../lib/LangContext";

export const Container = ({ children, className = "", ...rest }) => (
  <div className={`mx-auto w-full max-w-7xl px-5 lg:px-8 ${className}`} {...rest}>{children}</div>
);

export const Eyebrow = ({ children }) => (
  <span className="inline-flex items-center rounded-full border border-[#F95738]/25 bg-[#FFF3EF] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[#D8431F]">
    {children}
  </span>
);

export const SectionHeading = ({ eyebrow, title, body, align = "left", className = "" }) => {
  const { tr } = useLang();
  return (
  <div className={`${align === "center" ? "mx-auto max-w-2xl text-center" : "max-w-3xl"} ${className}`}>
    {eyebrow && <Eyebrow>{tr(eyebrow)}</Eyebrow>}
    <h2 className="mt-4 text-3xl font-bold text-slate-900 sm:text-4xl">{tr(title)}</h2>
    {body && <p className="mt-4 text-base leading-relaxed text-slate-600">{tr(body)}</p>}
  </div>
  );
};

export const PrimaryButton = ({ to, href, children, testId, className = "", ...rest }) => {
  const cls = `inline-flex items-center justify-center rounded-full bg-[#F95738] px-6 py-3.5 text-base font-semibold text-white shadow-[0_10px_30px_rgba(249,87,56,0.3)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#E0482B] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#F95738] ${className}`;
  if (to)
    return (
      <Link to={to} data-testid={testId} className={cls} {...rest}>
        {children}
      </Link>
    );
  return (
    <button type="button" data-testid={testId} className={cls} {...rest}>
      {children}
    </button>
  );
};

export const GhostButton = ({ to, children, testId, className = "", ...rest }) => {
  const cls = `inline-flex items-center justify-center rounded-full border border-slate-300 bg-white px-6 py-3.5 text-base font-semibold text-slate-800 transition-all duration-200 hover:-translate-y-0.5 hover:border-slate-400 ${className}`;
  if (to)
    return (
      <Link to={to} data-testid={testId} className={cls} {...rest}>
        {children}
      </Link>
    );
  return (
    <button type="button" data-testid={testId} className={cls} {...rest}>
      {children}
    </button>
  );
};

export const CtaBand = ({
  title = "One less thing on your to-do list.",
  body = "Let PostMint take care of your social content.",
  cta = "Get Started",
  to = "/get-started",
  testId = "cta-band-button",
}) => {
  const { tr } = useLang();
  return (
  <Container className="py-20 sm:py-24">
    <div className="relative overflow-hidden rounded-3xl bg-[#0F172A] px-6 py-16 text-center sm:px-12">
      <div
        className="pointer-events-none absolute inset-0 opacity-70"
        style={{
          backgroundImage:
            "radial-gradient(40rem 20rem at 80% 0%, rgba(249,87,56,0.35), transparent 60%), radial-gradient(30rem 18rem at 10% 100%, rgba(255,196,61,0.22), transparent 60%)",
        }}
        aria-hidden="true"
      />
      <div className="relative">
        <h2 className="mx-auto max-w-2xl text-3xl font-bold text-white sm:text-4xl">{tr(title)}</h2>
        <p className="mx-auto mt-4 max-w-xl text-base text-slate-300">{tr(body)}</p>
        <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <PrimaryButton to={to} testId={testId}>
            {tr(cta)}
          </PrimaryButton>
          <Link
            to="/pricing"
            data-testid="cta-band-pricing-link"
            className="rounded-full border border-white/25 px-6 py-3.5 text-base font-semibold text-white transition-colors hover:bg-white/10"
          >
            {tr("See Pricing")}
          </Link>
        </div>
      </div>
    </div>
  </Container>
  );
};
